import { useCallback } from 'react';

const KEYBOARD_STEP_PX = 24;

interface DrawerResizeHandleProps {
  width: number;
  onWidthChange: (width: number) => void;
  onResizingChange: (resizing: boolean) => void;
  onReset: () => void;
}

/** Draggable left edge of the drawer (pointer, keyboard arrows, double-click to reset). */
export const DrawerResizeHandle: React.FC<DrawerResizeHandleProps> = ({
  width,
  onWidthChange,
  onResizingChange,
  onReset,
}) => {
  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      const target = event.currentTarget;
      target.setPointerCapture(event.pointerId);
      onResizingChange(true);

      const move = (moveEvent: PointerEvent) =>
        onWidthChange(window.innerWidth - moveEvent.clientX);
      const end = () => {
        target.removeEventListener('pointermove', move);
        target.removeEventListener('pointerup', end);
        target.removeEventListener('pointercancel', end);
        onResizingChange(false);
      };
      target.addEventListener('pointermove', move);
      target.addEventListener('pointerup', end);
      target.addEventListener('pointercancel', end);
    },
    [onWidthChange, onResizingChange]
  );

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') onWidthChange(width + KEYBOARD_STEP_PX);
    else if (event.key === 'ArrowRight') onWidthChange(width - KEYBOARD_STEP_PX);
    else return;
    event.preventDefault();
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize assistant panel"
      tabIndex={0}
      title="Drag to resize · double-click to reset"
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onDoubleClick={onReset}
      className="group absolute inset-y-0 left-0 z-10 w-1.5 -translate-x-1/2 cursor-col-resize touch-none select-none focus:outline-none"
    >
      <div className="mx-auto h-full w-0.5 bg-transparent transition-colors group-hover:bg-blue-400 group-focus-visible:bg-blue-400 group-active:bg-blue-500" />
    </div>
  );
};
